import type { RecognitionMatchState, RecognitionOutcomeCell, RecognitionScanIssue } from '../types';

/**
 * Reviewer-facing Recognition review taxonomy and outcome-cell vocabulary.
 *
 * Everything here mirrors `fsim/models/forge/recognition_review.py` verbatim: the bucket paths,
 * the six outcome cells and their triage weights are code-defined and versioned on the backend, so
 * this module restates them rather than deriving them at runtime. Keep the two in sync.
 */

export type RecognitionBucketKey =
  | 'zero_crops'
  | 'not_enough_crops'
  | 'dirty_crops'
  | 'design_group_missing'
  | 'design_group_wrong'
  | 'ssm'
  | 'vizsim'
  | 'image_quality'
  | 'catalog_resolution'
  | 'other';

/** One selectable entry in the bucket picker; the same shape serves both taxonomy levels. */
export interface RecognitionBucketOption {
  /** Full bucket path, e.g. `zero_crops` or `zero_crops/aged_product`. */
  readonly path: string;
  readonly label: string;
  /** Single letter, unique within its own level, used by the review tool's chooser. */
  readonly shortcutKey: string;
  /**
   * One sentence naming the cause, shown as the picker option's tooltip.
   *
   * The picker shows every leaf of the taxonomy at once, so the labels alone are too terse to
   * separate neighbours like "Design group missing / Partial" from "Design group wrong / Partial".
   * This is the text that does the separating.
   */
  readonly description: string;
}

export interface RecognitionBucketDefinition extends RecognitionBucketOption {
  readonly bucket: RecognitionBucketKey;
  /**
   * The team that would own the remediation if this bucket were the primary bucket. The primary
   * bucket answers "what single change would most likely have made this sample confident correct",
   * so every top-level bucket names exactly one owner.
   */
  readonly owner: string;
  readonly subBuckets: readonly RecognitionBucketOption[];
}

/** The `other/unknown` bucket requires a free-text note. */
export const RECOGNITION_OTHER_BUCKET: RecognitionBucketKey = 'other';

export const RECOGNITION_ISSUE_BUCKETS: readonly RecognitionBucketDefinition[] = [
  {
    bucket: 'zero_crops',
    path: 'zero_crops',
    label: 'Zero crops',
    shortcutKey: 'Z',
    description:
      'The truth product has no active reference crops at all, so Recognition had nothing to match it against.',
    owner: 'Collection',
    subBuckets: [
      {
        path: 'zero_crops/new_product',
        label: 'New product',
        shortcutKey: 'N',
        description: 'The product is new, so no crops have been collected for it yet.',
      },
      {
        path: 'zero_crops/aged_product',
        label: 'Aged product',
        shortcutKey: 'A',
        description:
          'The product is not new, yet its crops were never collected or were all deactivated.',
      },
      {
        path: 'zero_crops/unknown',
        label: "I don't know",
        shortcutKey: '?',
        description: 'The cause sits in this category, but the evidence does not say which one.',
      },
    ],
  },
  {
    bucket: 'not_enough_crops',
    path: 'not_enough_crops',
    label: 'Not enough crops',
    shortcutKey: 'N',
    description:
      'Reference crops exist, but none of the design, facing, or conditions this capture shows.',
    owner: 'Collection',
    subBuckets: [
      {
        path: 'not_enough_crops/new_design',
        label: 'New design',
        shortcutKey: 'D',
        description: 'Crops exist for the product, but none of the packaging design on this shelf.',
      },
      {
        path: 'not_enough_crops/new_orientation',
        label: 'New orientation',
        shortcutKey: 'O',
        description: 'Crops exist for the design, but none from the facing the capture shows.',
      },
      {
        path: 'not_enough_crops/new_camera_conditions',
        label: 'New camera conditions',
        shortcutKey: 'C',
        description: 'Crops exist, but none under this lighting, distance, or camera.',
      },
      {
        path: 'not_enough_crops/unknown',
        label: "I don't know",
        shortcutKey: '?',
        description: 'The cause sits in this category, but the evidence does not say which one.',
      },
    ],
  },
  {
    bucket: 'dirty_crops',
    path: 'dirty_crops',
    label: 'Dirty crops',
    shortcutKey: 'D',
    description:
      "The truth product's reference crops carry another product, so the match was pulled off it.",
    owner: 'Labeling provenance',
    subBuckets: [
      {
        path: 'dirty_crops/store_dc_error',
        label: 'Store DC error',
        shortcutKey: 'S',
        description: 'A store data-collection scan attached the wrong product to a crop.',
      },
      {
        path: 'dirty_crops/remote_dc_error',
        label: 'Remote DC error',
        shortcutKey: 'R',
        description: 'A Remote DC labeler attached the wrong product to a crop.',
      },
      {
        path: 'dirty_crops/cleaning_error',
        label: 'Cleaning error',
        shortcutKey: 'C',
        description:
          'A cleaning pass left the wrong crops on this product, or moved the right ones away.',
      },
      {
        path: 'dirty_crops/unknown',
        label: "I don't know",
        shortcutKey: '?',
        description: 'The cause sits in this category, but the evidence does not say which one.',
      },
    ],
  },
  {
    bucket: 'design_group_missing',
    path: 'design_group_missing',
    label: 'Design group missing',
    shortcutKey: 'G',
    description: 'The design group link that would have made this match correct does not exist.',
    owner: 'Grouping',
    subBuckets: [
      {
        path: 'design_group_missing/full',
        label: 'Full',
        shortcutKey: 'F',
        description:
          'A full link is missing: the predicted design and the truth are the same product.',
      },
      {
        path: 'design_group_missing/partial',
        label: 'Partial',
        shortcutKey: 'P',
        description:
          'A partial link is missing: the designs share a product but differ in size, flavour, or pack. Truly indistinguishable lookalikes belong here too.',
      },
      {
        path: 'design_group_missing/non_front',
        label: 'Non-front',
        shortcutKey: 'N',
        description:
          'A non-front link is missing: the prediction shows another face of the same product.',
      },
      {
        path: 'design_group_missing/unknown',
        label: "I don't know",
        shortcutKey: '?',
        description: 'The cause sits in this category, but the evidence does not say which one.',
      },
    ],
  },
  {
    bucket: 'design_group_wrong',
    path: 'design_group_wrong',
    label: 'Design group wrong',
    shortcutKey: 'W',
    description:
      'A design group link exists but is incorrect, so it credits or blames the wrong identity.',
    owner: 'Grouping',
    subBuckets: [
      {
        path: 'design_group_wrong/full',
        label: 'Full',
        shortcutKey: 'F',
        description: 'A full link joins two designs that are not the same product.',
      },
      {
        path: 'design_group_wrong/partial',
        label: 'Partial',
        shortcutKey: 'P',
        description: 'A partial link joins designs that should not be linked at all.',
      },
      {
        path: 'design_group_wrong/non_front',
        label: 'Non-front',
        shortcutKey: 'N',
        description:
          'A non-front link points at a different product rather than another face of this one.',
      },
      {
        path: 'design_group_wrong/unknown',
        label: "I don't know",
        shortcutKey: '?',
        description: 'The cause sits in this category, but the evidence does not say which one.',
      },
    ],
  },
  {
    bucket: 'ssm',
    path: 'ssm',
    label: 'SSM detection',
    shortcutKey: 'S',
    description:
      'The detector drew the polygon; the polygon itself is the problem, not the recognition.',
    owner: 'Detection',
    subBuckets: [
      {
        path: 'ssm/person_detection',
        label: 'Person detection',
        shortcutKey: 'P',
        description: 'The polygon covers a person or a body part, not a product on the shelf.',
      },
      {
        path: 'ssm/unnecessary_polygon',
        label: 'Unnecessary polygon',
        shortcutKey: 'U',
        description: 'The polygon covers something that should never have been detected.',
      },
      {
        path: 'ssm/bad_polygon',
        label: 'Bad polygon',
        shortcutKey: 'B',
        description:
          'The polygon covers the wrong area: cut off, merged with a neighbour, or off the product.',
      },
      {
        path: 'ssm/unknown',
        label: "I don't know",
        shortcutKey: '?',
        description: 'The cause sits in this category, but the evidence does not say which one.',
      },
    ],
  },
  {
    bucket: 'vizsim',
    path: 'vizsim',
    label: 'VizSim model',
    shortcutKey: 'V',
    description:
      'Crops, groups, and the polygon are all sound; the embedding model still ranked another item first.',
    owner: 'Recognition model',
    subBuckets: [
      {
        path: 'vizsim/stale_train',
        label: 'Stale train',
        shortcutKey: 'S',
        description: "The model was trained before this product's crops existed.",
      },
      {
        path: 'vizsim/not_in_train',
        label: 'Not in train',
        shortcutKey: 'N',
        description: "The product's crops exist but never entered the training set.",
      },
      {
        path: 'vizsim/text_recognition',
        label: 'Text recognition',
        shortcutKey: 'T',
        description:
          'Telling the products apart needs the text on the pack read, which the model does poorly.',
      },
      {
        path: 'vizsim/small_detail',
        label: 'Small detail',
        shortcutKey: 'D',
        description: 'The products differ only in a detail too small for the embedding to carry.',
      },
      {
        path: 'vizsim/variable_conditions',
        label: 'Variable conditions',
        shortcutKey: 'V',
        description:
          'The crops are sound; the model failed under this lighting, angle, or distance.',
      },
      {
        path: 'vizsim/unknown',
        label: "I don't know",
        shortcutKey: '?',
        description: 'The cause sits in this category, but the evidence does not say which one.',
      },
    ],
  },
  {
    bucket: 'image_quality',
    path: 'image_quality',
    label: 'Image quality',
    shortcutKey: 'I',
    description: 'The capture itself is too poor for any model to recognise the product.',
    owner: 'Capture',
    subBuckets: [
      {
        path: 'image_quality/blurry',
        label: 'Blurry',
        shortcutKey: 'B',
        description: 'The capture is out of focus or motion-blurred.',
      },
      {
        path: 'image_quality/overexposed',
        label: 'Overexposed',
        shortcutKey: 'O',
        description: 'Light washes the pack out of the capture.',
      },
      {
        path: 'image_quality/underexposed',
        label: 'Underexposed',
        shortcutKey: 'U',
        description: 'The capture is too dark to read the pack.',
      },
      {
        path: 'image_quality/occluded',
        label: 'Occluded',
        shortcutKey: 'C',
        description: 'Something in front of the product blocks it in the capture.',
      },
      {
        path: 'image_quality/unknown',
        label: "I don't know",
        shortcutKey: '?',
        description: 'The cause sits in this category, but the evidence does not say which one.',
      },
    ],
  },
  {
    bucket: 'catalog_resolution',
    path: 'catalog_resolution',
    label: 'Catalog resolution',
    shortcutKey: 'C',
    description:
      'The barcode-to-catalog mapping failed or is wrong, so the identity never resolved correctly.',
    owner: 'Catalog',
    subBuckets: [
      {
        path: 'catalog_resolution/unresolved_barcode',
        label: 'Unresolved barcode',
        shortcutKey: 'B',
        description: 'The barcode is well-formed but no catalog item stands behind it.',
      },
      {
        path: 'catalog_resolution/wrong_mapping',
        label: 'Wrong mapping',
        shortcutKey: 'M',
        description: 'The barcode resolves, but to the wrong catalog item.',
      },
      {
        path: 'catalog_resolution/unknown',
        label: "I don't know",
        shortcutKey: '?',
        description: 'The cause sits in this category, but the evidence does not say which one.',
      },
    ],
  },
  {
    bucket: 'other',
    path: 'other',
    label: 'Other',
    shortcutKey: 'O',
    description: 'None of the categories above fits; the required note carries the cause.',
    owner: 'Unassigned',
    subBuckets: [
      {
        path: 'other/unknown',
        label: "I don't know",
        shortcutKey: '?',
        description: 'None of the categories fits this sample; describe the cause in the note.',
      },
    ],
  },
];

/** Level-one picker options, in taxonomy order. */
export const RECOGNITION_BUCKET_OPTIONS: readonly RecognitionBucketOption[] =
  RECOGNITION_ISSUE_BUCKETS.map(({ path, label, shortcutKey, description }) => ({
    path,
    label,
    shortcutKey,
    description,
  }));

const BUCKETS_BY_KEY = new Map<string, RecognitionBucketDefinition>(
  RECOGNITION_ISSUE_BUCKETS.map((definition) => [definition.bucket, definition])
);

/** Return the top-level bucket of a bucket path (`zero_crops/aged_product` -> `zero_crops`). */
export function bucketPrefix(path: string): string {
  return path.split('/')[0] ?? '';
}

/** Return the level-two picker options for one top-level bucket; `[]` when it has none. */
export function bucketOptionsFor(prefix: string): readonly RecognitionBucketOption[] {
  return BUCKETS_BY_KEY.get(bucketPrefix(prefix))?.subBuckets ?? [];
}

/** Return the definition for a bucket path's top-level bucket, or `null` when it is unknown. */
export function bucketDefinitionFor(path: string): RecognitionBucketDefinition | null {
  return BUCKETS_BY_KEY.get(bucketPrefix(path)) ?? null;
}

/** True when `path` is a complete known bucket/sub-bucket path. */
export function isValidBucketPath(path: string): boolean {
  const definition = BUCKETS_BY_KEY.get(bucketPrefix(path));
  if (!definition) return false;
  if (!path.includes('/')) return false;
  return definition.subBuckets.some((option) => option.path === path);
}

/**
 * Human label for a complete bucket path.
 */
export function bucketLabel(path: string): string {
  const definition = BUCKETS_BY_KEY.get(bucketPrefix(path));
  if (!definition) return path;
  if (!path.includes('/')) return definition.label;
  const subBucket = definition.subBuckets.find((option) => option.path === path);
  return subBucket ? `${definition.label} · ${subBucket.label}` : path;
}

/**
 * Human label for one sub-bucket key of `bucket`, as the aggregates endpoint spells it.
 *
 * That endpoint emits BARE sub-bucket names (`stale_train`, not `vizsim/stale_train`) because it
 * partitions the stored path. `bucketLabel` only resolves full paths, so a bare name has to be
 * re-joined to its bucket before it can be named. The label is the sub-bucket's own, not the joined path:
 * these are rendered nested under a row that already carries the bucket label.
 *
 * @param bucket The row's bucket path, used as the prefix a bare sub-bucket name belongs to.
 * @param subBucket The sub-bucket key to name; bare, `(unspecified)`, or already a full path.
 * @returns The reviewer-facing label, falling back to the raw key when the taxonomy has no entry.
 */
export function subBucketLabel(bucket: string, subBucket: string): string {
  if (subBucket.includes('/')) {
    return bucketLabel(subBucket);
  }
  const prefix = bucketPrefix(bucket);
  const option = BUCKETS_BY_KEY.get(prefix)?.subBuckets.find(
    (candidate) => candidate.path === `${prefix}/${subBucket}`
  );
  return option?.label ?? subBucket;
}

/** Six outcome cells, best triage score first, used for stable ordering in grids and bars. */
export const RECOGNITION_CELL_ORDER: readonly RecognitionOutcomeCell[] = [
  'confident_correct',
  'unconfident_correct',
  'confident_adjusted',
  'unconfident_adjusted',
  'unconfident_incorrect',
  'confident_incorrect',
];

export const RECOGNITION_CELL_LABELS: Record<RecognitionOutcomeCell, string> = {
  confident_correct: 'Confident correct',
  unconfident_correct: 'Unconfident correct',
  confident_adjusted: 'Confident adjusted',
  unconfident_adjusted: 'Unconfident adjusted',
  unconfident_incorrect: 'Unconfident incorrect',
  confident_incorrect: 'Confident incorrect',
};

export type RecognitionCellTone = 'positive' | 'muted' | 'warning' | 'danger';

/**
 * Only confident correct is a success; adjusted always reads as a warning and incorrect as a
 * failure regardless of confidence, while unconfident correct stays muted because it is a
 * near-miss rather than a win.
 */
export const RECOGNITION_CELL_TONE: Record<RecognitionOutcomeCell, RecognitionCellTone> = {
  confident_correct: 'positive',
  unconfident_correct: 'muted',
  confident_adjusted: 'warning',
  unconfident_adjusted: 'warning',
  unconfident_incorrect: 'danger',
  confident_incorrect: 'danger',
};

/** Per-cell triage weights; 1.0 is healthy and 0.0 is worst. Mirrors `TRIAGE_SCORE_BY_CELL`. */
export const RECOGNITION_TRIAGE_SCORE_BY_CELL: Record<RecognitionOutcomeCell, number> = {
  confident_correct: 1.0,
  unconfident_correct: 0.7,
  confident_adjusted: 0.5,
  unconfident_adjusted: 0.5,
  unconfident_incorrect: 0.3,
  confident_incorrect: 0.0,
};

/** The only supported spelling of an outcome-cell key; mirrors the backend `cell_key` helper. */
export function cellKey(
  confident: boolean,
  matchState: RecognitionMatchState
): RecognitionOutcomeCell {
  return `${confident ? 'confident' : 'unconfident'}_${matchState}` as RecognitionOutcomeCell;
}

/** Display order for the derived collection-side scan issues; mirrors `RecognitionScanIssue`. */
export const RECOGNITION_SCAN_ISSUE_ORDER: readonly RecognitionScanIssue[] = [
  'missing_scan',
  'wrong_scan',
  'scan_unresolved',
];

export const RECOGNITION_SCAN_ISSUE_LABELS: Record<RecognitionScanIssue, string> = {
  missing_scan: 'Missing scan',
  wrong_scan: 'Wrong scan',
  scan_unresolved: 'Unresolved scan',
};

/** Name of the default Recognition confidence profile shown beside the info control. */
export const RECOGNITION_CONFIDENCE_PROFILE_LABEL = 'Recommended';

/** Exponent of the VizSim2 `C_0.69` confidence formula. Mirrors `CONFIDENCE_EXPONENT`. */
export const RECOGNITION_CONFIDENCE_EXPONENT = 0.69;

/** Raw cosine spelling of the threshold. Mirrors `RAW_COSINE_CONFIDENCE_THRESHOLD`. */
export const RECOGNITION_CONFIDENCE_THRESHOLD_RAW_COSINE = 0.35;

/**
 * Confident-versus-unconfident cut on the Atlas display scale.
 *
 * Derived from the same two constants the backend derives `ATLAS_CONFIDENCE_THRESHOLD` from rather
 * than restated as a rounded literal: a sample sitting between the rounded value and the exact one
 * would otherwise be shaded confident here and stored unconfident there. It is not a similarity
 * conversion - the raw cosine threshold is the equivalent cut on the other scale.
 */
export const RECOGNITION_CONFIDENCE_THRESHOLD =
  RECOGNITION_CONFIDENCE_THRESHOLD_RAW_COSINE / 2 ** (1 - RECOGNITION_CONFIDENCE_EXPONENT);

/** Substitute for `s2` when captured results hold no different-item-number candidate. */
export const RECOGNITION_RUNNER_UP_FLOOR = 0.65;

/** `C_0.69 = (s1 - s2) / (1 - s2)^0.69`, on whichever score scale `s1` and `s2` share. */
export function recognitionConfidenceValue(s1: number, s2: number): number | null {
  if (!Number.isFinite(s1) || !Number.isFinite(s2) || s2 >= 1) return null;
  return (s1 - s2) / (1 - s2) ** RECOGNITION_CONFIDENCE_EXPONENT;
}

/** True when the display-scale scores clear the versioned confidence threshold. */
export function isRecognitionConfident(s1: number, s2: number): boolean {
  const value = recognitionConfidenceValue(s1, s2);
  return value !== null && value >= RECOGNITION_CONFIDENCE_THRESHOLD;
}
